import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import TokenDialog from "./TokenDialog";
import ThemeToggle from "./ThemeToggle";
import { createThirdwebClient } from "thirdweb";
import { ConnectButton } from "thirdweb/react";
import { darkTheme, lightTheme } from "thirdweb/react";
import { baseSepolia } from "thirdweb/chains";
import { LuMenu, LuX } from "react-icons/lu";
import { useTheme } from "../contexts/ThemeContext";
import { useIsMobile } from "../hooks/useMediaQuery";
import { BRIDGE_CHAINS, connectModalCopy, getWallets } from "../hooks/useConnectWallet";
import { NavbarItem } from "./navbar/NavbarItem";
import { useNavItems } from "./navbar/useNavItems";

/* Same artwork as the brand mark, already cropped small — the 512px logo is
   wasted on a 36px slot. */
const logoMark = "/usdx-icon.png";

/* Hex mirrors of the --u-* tokens: thirdweb's theme API takes plain colours,
   so it cannot read the CSS variables the rest of the app is driven by. */
const PALETTE = {
    light: {
        accent: "#066FD2",
        accentStrong: "#0A5AA8",
        onAccent: "#FFFFFF",
        surface: "#FFFFFF",
        surfaceMuted: "#F5F9FD",
        surfaceSunken: "#EEF4FA",
        line: "#C6D8E8",
        ink: "#0B2545",
    },
    dark: {
        accent: "#5CACF2",
        accentStrong: "#74B8F2",
        onAccent: "#0A1626",
        surface: "#12233A",
        surfaceMuted: "#172C45",
        surfaceSunken: "#1C3149",
        line: "#24374F",
        ink: "#E9F1F9",
    },
};

/* The wallet pill is fixed navy in both themes so it stays the one anchor of
   colour in the bar, matching the rest of the ecosystem. */
const WALLET_GRADIENT = "linear-gradient(to right, #041A3B 0%, #10479B 100%)";

const WALLET_PILL_STYLE = {
    height: "40px",
    minHeight: "40px",
    minWidth: "unset",
    padding: "0 20px",
    borderRadius: "9999px",
    background: WALLET_GRADIENT,
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
};

/* Connected state carries a 35px avatar plus two lines of text, so it sets its
   own padding instead of the CTA's fixed height. Its label colours come from
   thirdweb's theme, which cannot see this gradient — .tw-connected-wallet__*
   in index.css pins them to white. */
const WALLET_DETAILS_STYLE = {
    minWidth: "unset",
    padding: "4px 16px 4px 5px",
    borderRadius: "9999px",
    background: WALLET_GRADIENT,
    color: "#FFFFFF",
    border: "none",
};

const Header = () => {
    /* useNavigate returns with the tab switcher below. */
    const navItems = useNavItems();
    /* thirdweb connects and auto-reconnects on the first of `chains`, which
       pushes the wallet to Base Sepolia (the DEX's network). The bridge is
       live on mainnets and asks for the chain each route needs, so there the
       wallet is left on whatever network it is on. */
    const onBridge = useLocation().pathname.startsWith("/bridge");
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [, setSelected] = useState("ETH");
    const { theme } = useTheme();
    const isMobile = useIsMobile();

    const isDark = theme === "dark";
    const c = isDark ? PALETTE.dark : PALETTE.light;

    // Close the mobile menu once the viewport is wide enough for the inline nav.
    // Threshold matches the `nav` screen in tailwind.config.js.
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 1200) setIsMobileOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const client = createThirdwebClient({
        clientId: process.env.REACT_APP_THIRD_WEB_CLIENT,
    });

    const themeCreator = isDark ? darkTheme : lightTheme;
    const customTheme = themeCreator({
        colors: {
            primaryButtonBg: c.accent,
            primaryButtonText: c.onAccent,
            modalBg: c.surface,
            borderColor: c.line,
            accentText: c.accent,
            separatorLine: c.line,
            secondaryButtonBg: c.surfaceSunken,
            secondaryButtonHoverBg: c.surfaceMuted,
            secondaryButtonText: c.accent,
            connectedButtonBg: c.surface,
            connectedButtonBgHover: c.surfaceSunken,
        },
    });

    /* In-app section tabs are parked while the bridge is the whole app. The
       routes still resolve, so restoring this array and the <nav> below brings
       Dex / Pool / Bridge back — re-check the `nav` screen in tailwind.config.js
       if you do, since each tab costs the header roughly 60px.
    const tabs = [
        { label: "Dex", path: "/swap", isActive: pathname.startsWith("/swap") },
        {
            label: "Pool",
            path: "/pool",
            isActive: pathname.startsWith("/pool") || pathname.includes("/remove"),
        },
        { label: "Bridge", path: "/bridge", isActive: pathname.startsWith("/bridge") },
    ];
    */

    return (
        <header className="sticky top-0 z-40 w-full header-3d">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
                {/* Brand */}
                <Link
                    to="/"
                    aria-label="USDX Smart"
                    className="flex shrink-0 items-center gap-2"
                >
                    <img className="h-9 w-9" src={logoMark} alt="" aria-hidden="true" />
                    <span className="flex flex-col justify-center leading-none">
                        <span
                            style={{ color: "#0d8ce0" }}
                            className="text-xl font-extrabold tracking-tight"
                        >
                            USDX
                        </span>
                        {/* Letters are spread edge-to-edge so SMART lines up
                            exactly with the width of USDX above it. */}
                        <span
                            style={{ color: "#2b9be8" }}
                            aria-hidden="true"
                            className="mt-1 flex w-full justify-between text-[9px] font-semibold uppercase"
                        >
                            {["S", "M", "A", "R", "T"].map((letter) => (
                                <span key={letter}>{letter}</span>
                            ))}
                        </span>
                    </span>
                </Link>

                {/* Ecosystem nav */}
                <nav className="hidden flex-1 items-center justify-center gap-1 nav:flex">
                    {navItems.map((item) => (
                        <NavbarItem key={item.label} item={item} />
                    ))}
                </nav>

                <div className="flex shrink-0 items-center gap-2">
                    <ConnectButton
                        connectButton={{
                            label: "Connect",
                            style: WALLET_PILL_STYLE,
                        }}
                        connectModal={{
                            /* "wide" renders a two-column modal with the welcome
                               panel beside the wallet list — it does not fit a phone,
                               so drop to the single-column layout there. */
                            size: isMobile ? "compact" : "wide",
                            ...connectModalCopy,
                        }}
                        detailsButton={{ style: WALLET_DETAILS_STYLE }}
                        wallets={getWallets()}
                        chains={onBridge ? BRIDGE_CHAINS : [baseSepolia]}
                        client={client}
                        theme={customTheme}
                        autoConnect={true}
                    />

                    <ThemeToggle />

                    <button
                        type="button"
                        aria-label="Toggle navigation menu"
                        aria-expanded={isMobileOpen}
                        onClick={() => setIsMobileOpen(!isMobileOpen)}
                        className="rounded-md p-2 text-usdx-ink-muted transition-colors duration-150 hover:bg-usdx-surface-sunken hover:text-usdx-ink nav:hidden"
                    >
                        {isMobileOpen ? (
                            <LuX className="h-6 w-6" />
                        ) : (
                            <LuMenu className="h-6 w-6" />
                        )}
                    </button>

                    <TokenDialog open={open} setOpen={setOpen} onClick={setSelected} />
                </div>
            </div>

            {/* Mobile nav */}
            <div
                className={`overflow-hidden bg-usdx-surface transition-[max-height] duration-300 ease-in-out nav:hidden ${
                    isMobileOpen ? "max-h-[32rem] border-t border-usdx-line" : "max-h-0"
                }`}
            >
                <nav className="space-y-1 px-4 py-3 sm:px-6">
                    {navItems.map((item) => (
                        <NavbarItem
                            key={item.label}
                            item={item}
                            variant="mobile"
                            onClick={() => setIsMobileOpen(false)}
                        />
                    ))}
                </nav>
            </div>
        </header>
    );
};

export default Header;
